import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, Bell, LogOut, Search, ShieldAlert, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NAV_GROUPS } from "@/lib/modules";
import { InstallButton } from "@/components/PWAInstall";
import { TrialBanner } from "@/components/PaywallGate";
import { initials, titleCase } from "@/lib/format";
import { logoSrc } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

function SidebarContent({ has, org, onNavigate }) {
  return (
    <div className="flex h-full flex-col bg-slate-900 text-slate-300">
      <div className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
        <img src={org?.logo ? logoSrc(org.logo) : "/icon-192.png"} alt="FHAPAS" onError={(e) => { e.currentTarget.src = "/icon-192.png"; }} className="h-9 w-9 rounded-lg bg-white object-contain p-0.5" />
        <div className="min-w-0">
          <p className="truncate font-display text-sm font-bold text-white">FHAPAS</p>
          <p className="truncate text-[11px] text-slate-400">{org?.name || "Fendana Health"}</p>
        </div>
      </div>
      <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
        {NAV_GROUPS.map((group) => {
          const items = group.items.filter((it) => has(it.permission));
          if (items.length === 0) return null;
          return (
            <div key={group.label}>
              <p className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-500">{group.label}</p>
              <div className="space-y-0.5">
                {items.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    onClick={onNavigate}
                    data-testid={`nav-${it.to.split("/").pop()}`}
                    className={({ isActive }) =>
                      `group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                        isActive ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && <span className="absolute inset-y-1.5 left-0 w-[3px] rounded-full bg-teal-400" />}
                        <it.icon className="h-[18px] w-[18px] shrink-0" />
                        <span className="truncate">{it.label}</span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>
    </div>
  );
}

export function StaffLayout({ children }) {
  const { user, org, has, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 lg:block">
        <div className="fixed bottom-0 left-0 top-[var(--ticker-h,0px)] w-64">
          <SidebarContent has={has} org={org} />
        </div>
      </aside>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-sm lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
              transition={{ type: "spring", damping: 26, stiffness: 260 }}
              className="fixed bottom-0 left-0 top-[var(--ticker-h,0px)] z-50 w-64 lg:hidden"
            >
              <button onClick={() => setMobileOpen(false)} className="absolute -right-10 top-3 rounded-lg bg-white/10 p-2 text-white">
                <X className="h-5 w-5" />
              </button>
              <SidebarContent has={has} org={org} onNavigate={() => setMobileOpen(false)} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-[var(--ticker-h,0px)] z-30 flex h-16 items-center gap-3 border-b border-slate-200 glass px-4 lg:px-6">
          <button data-testid="mobile-menu-button" className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden" onClick={() => setMobileOpen(true)}>
            <Menu className="h-5 w-5" />
          </button>
          <div className="hidden items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-400 sm:flex">
            <Search className="h-4 w-4" /> <span className="pr-8">Search patients, staff…</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <InstallButton />
            {user?.role === "superadmin" && (
              <a href="/superadmin" data-testid="goto-superadmin" className="hidden items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 sm:flex">
                <ShieldAlert className="h-4 w-4" /> Superadmin
              </a>
            )}
            <NavLink to="/app/notifications" data-testid="nav-notifications" className="relative rounded-lg p-2 text-slate-600 hover:bg-slate-100">
              <Bell className="h-5 w-5" />
            </NavLink>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-lg py-1 pl-1 pr-2 hover:bg-slate-100">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-teal-700 text-xs font-bold text-white">{initials(user?.full_name)}</span>
                  <span className="hidden text-left sm:block">
                    <span className="block text-sm font-semibold leading-tight text-slate-800">{user?.full_name}</span>
                    <span className="block text-[11px] leading-tight text-slate-500">{titleCase(user?.role || "")}</span>
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild><NavLink to="/app/settings">Organisation settings</NavLink></DropdownMenuItem>
                <DropdownMenuItem asChild><NavLink to="/app/subscription">Subscription</NavLink></DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-rose-600 focus:text-rose-600">
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main key={location.pathname} className="animate-fade-up mx-auto w-full max-w-[1600px] flex-1 px-4 py-6 lg:px-8">
          <TrialBanner />
          {children}
        </main>
      </div>
    </div>
  );
}
